import React, { useContext, useMemo } from "react";
import { AuthProvider, useAuth } from "./AuthContext";
import { ReleasesProvider, useReleases, ReleasesContext } from "./ReleasesContext";
import { LabelsProvider, useLabels } from "./LabelsContext";

// Re-export the hooks for backward compatibility
export { useAuth } from "./AuthContext";
export { useLabels } from "./LabelsContext";
export { useReleases } from "./ReleasesContext";

// Combined hook for components that need multiple contexts
export const useAppContext = () => {
  const auth = useAuth();
  const labels = useLabels();
  const releases = useReleases();
  
  return {
    // Auth context
    userId: auth.userId,
    user: auth.user,
    isAuthenticated: auth.isAuthenticated,
    isAuthLoading: auth.isAuthLoading,
    signOut: auth.signOut,
    
    // Labels context
    paginatedLabels: labels.paginatedLabels,
    setLabelPage: labels.setLabelPage,
    searchTerm: labels.searchTerm,
    setSearchTerm: labels.setSearchTerm,
    isLoading: labels.isLoading,
    
    // Releases context
    checkedReleases: releases.checkedReleases,
    toggleListen: releases.toggleListen,
    isSubmitting: releases.isSubmitting,
    loadingReleaseId: releases.loadingReleaseId,
    isReleaseChecked: releases.isReleaseChecked,
    
    // Combined error handling
    error: releases.error || labels.error
  };
};

// Combined provider that wraps all the individual providers
export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <AuthProvider>
      <ReleasesProvider>
        <ConnectedLabelsProvider>
          {children}
        </ConnectedLabelsProvider>
      </ReleasesProvider>
    </AuthProvider>
  );
};

// Helper component to connect the ReleasesContext to LabelsProvider
const ConnectedLabelsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const releasesContext = useContext(ReleasesContext);
  
  // Memoize the checkedReleases to prevent unnecessary re-renders
  const checkedReleases = useMemo(() => releasesContext.checkedReleases, [releasesContext.checkedReleases]);
  
  return (
    <LabelsProvider checkedReleases={checkedReleases}>
      {children}
    </LabelsProvider>
  );
};

// For backward compatibility
export const AppContext = React.createContext<any>(null);
